/**
 * @file services/funds.js
 * @since v1.0
 * @author Carlos Lenon <code@carloslenon.com>
 */

// #region common
import BaseServiceRetryWrapper from './baseRetry';
import { FUNDS, MEASUREMENT_DATES } from '../common/endpoints';

// #endregion

class FundsService extends BaseServiceRetryWrapper {
  /**
   * @function
   * @name getFundById
   * @description Get the fund by its Id
   * @param {integer}
   * @return {object}
   */
  getFundById = (fundId: number) => this.get(FUNDS.fundById(fundId));

  /**
   * @function
   * @name getMeasurementDateByFundId
   * @description Get the list of Measurement Dates that belong to a Fund
   * @param {integer} fundId
   * @return {array} List of Measurement Dates
   */
  getMeasurementDateByFundId = (fundId: string) => this.get(MEASUREMENT_DATES.getMeasurementDateByFundId(fundId));

  /**
   * @function
   * @name getFundSummary
   * @description Get summary data for a fund, optionally filtered by date
   * @param {string} fundId - The fund ID
   * @param {string | null} date - Optional date (YYYY-MM-DD) to filter by, omit for most recent
   * @return {object}
   */
  getFundSummary = (fundId: string, date?: string | null) => this.get(FUNDS.getFundSummary(fundId, date));

  /**
   * @function
   * @name getFundInvestments
   * @description Get fund investments, optionally filtered by date
   * @param {string} fundId - The fund ID
   * @param {string | null} date - Optional date (YYYY-MM-DD) to filter by, omit for most recent
   * @param {boolean} checkForExport - Whether to check for export
   * @return {object}
   */
  getFundInvestments = (fundId: string, date?: string | null, checkForExport: boolean = false) =>
    this.get(FUNDS.getFundInvestments(fundId, date, checkForExport));

  /**
   * @function
   * @name newFund
   * @description create a new Fund.
   * @param {OBJECT} data The fund structure to send
   */
  newFund = (data: unknown) => this.post(FUNDS.newFund, data);

  /**
   * @function
   * @name updateFundById
   * @description Update an existent Fund.
   * @param {OBJECT} data The fund structure to send
   */
  updateFundById = (fundId: number, data: unknown) => this.put(FUNDS.fundById(fundId), data);

  /**
   * @function
   * @name createCompanyForMeasurementDate
   * @description Create new company and add it to a fund's measurement Date.
   * @param {OBJECT} data The company structure to send
   */
  createCompanyForMeasurementDate = (mpId: string, data: unknown) =>
    this.post(FUNDS.createCompanyForMeasurementDate(mpId), data);

  /**
   * @function
   * @name addCompanyToMeasurementDate
   * @description Add existing company to fund measurement Date.
   * @param {OBJECT} data The data structure to send
   */
  addCompanyToMeasurementDate = (data: unknown) => this.post(FUNDS.addCompanyToMeasurementDate, data);

  /**
   * @function
   * @name getFundAllocations
   * @description Get fund allocations, optionally filtered by date
   * @param {string} fundId - The fund ID
   * @param {string | null} date - Optional date (YYYY-MM-DD) to filter by, omit for most recent
   * @return {object}
   */
  getFundAllocations = (fundId: string, date?: string | null) => this.get(FUNDS.getFundAllocations(fundId, date));

  getCompaniesWithOwnershipByFund = (fundId: number): Promise<any> => this.get(FUNDS.getCompaniesByFundId(fundId));

  deleteFund = (fundId: string) => this.delete(FUNDS.fundById(fundId));
}

export default FundsService;
